import {gql} from '@apollo/client';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {SequencePreferences} from 'constants/SequenceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import WarningBanner from 'molbio/WarningBanner';
import usePreference from 'ui-library/hooks/usePreference';

import {
  ContainingSequencesQuery,
  ContainingSequencesQueryVariables,
} from './__generated/ContainingSequencesQuery';

export const CONTAINING_SEQUENCES_QUERY = gql`
  query ContainingSequencesQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    sequence(id: $id) {
      ... on SequenceNode {
        id
        containingSequences {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function PartsWarningBanner({sequenceId}: {sequenceId: string}): JSX.Element {
  const [collapsed, setCollapsed] = usePreference<boolean>(SequencePreferences.SHOULD_COLLAPSE_PARTS_WARNING);

  if (!BenchlingConfig.MOLBIO_ENABLE_PARTS_WARNING_BANNER) {
    return <div />;
  }
  return (
    <BenchlingQuery<ContainingSequencesQuery, ContainingSequencesQueryVariables>
      query={CONTAINING_SEQUENCES_QUERY}
      variables={{id: sequenceId}}
      renderLoading={() => <div />}
    >
      {(data: ContainingSequencesQuery) => {
        if (!isInaccessible(data.sequence)) {
          const numContainingSequences = data.sequence.containingSequences.length;
          if (numContainingSequences === 0) {
            return <div />;
          }
          const demonstrativeText = pluralize(numContainingSequences, 'that', 'those');
          const sequenceText = pluralize(numContainingSequences, 'sequence');
          const expandedMessage =
            BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS || BenchlingConfig.PARTS_ENABLE_CROSS_ORIGIN_PARTS
              ? ' Base edits are not allowed on sequences that are contained by other sequences.' +
                ' To change bases, please make a copy of this sequence instead, or unlink it from the containing' +
                ` ${pluralize(numContainingSequences, 'sequence')}.`
              : ` Editing this sequence will change ${demonstrativeText} ${numContainingSequences} containing ` +
                `${sequenceText}, and any additional sequences that reference ${demonstrativeText} ` +
                `containing ${sequenceText} as ${pluralize(numContainingSequences, 'a part', 'parts')}.`;

          const message = `This sequence is referenced as a part in ${numContainingSequences} containing ${sequenceText}.${
            collapsed ? '' : expandedMessage
          }`;
          return (
            <WarningBanner
              message={message}
              collapsed={collapsed}
              onToggleCollapsed={() => {
                setCollapsed(!collapsed);
              }}
            />
          );
        }
        return <div />;
      }}
    </BenchlingQuery>
  );
}

export default PartsWarningBanner;
